import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowRight, Play, Shield, Zap, Users, CalendarDays,
  Music2, Video, Menu, X, ChevronDown, Twitter,
  Linkedin, Instagram, Star, Award, ChevronRight, Clock, 
  FileText, Edit, Trash, Timer, Sparkles, TrendingUp,
  Globe, Layers, BarChart3, Cpu, Database, Rocket,
  Heart, Mic, Camera, Trophy, Volume2, CheckCircle, Target
} from 'lucide-react';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import { getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import { AUTH_API, fetchWithAuth } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import ddLogo from './ui/dd.png';

// Dynamic floating elements with dance-inspired movement
const DanceParticles = () => {
  const createParticle = (index) => {
    // Create a grid-based distribution with some randomness
    const cols = 10;
    const rows = 3;
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    const baseX = (col / cols) * (typeof window !== 'undefined' ? window.innerWidth : 1200);
    const baseY = (row / rows) * (typeof window !== 'undefined' ? window.innerHeight : 800);
    
    // Add some randomness to avoid perfect grid
    const randomOffsetX = (Math.random() - 0.5) * ((typeof window !== 'undefined' ? window.innerWidth : 1200) / cols);
    const randomOffsetY = (Math.random() - 0.5) * ((typeof window !== 'undefined' ? window.innerHeight : 800) / rows);
    
    return {
      startX: baseX + randomOffsetX,
      startY: baseY + randomOffsetY
    };
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
      {[...Array(30)].map((_, i) => {
        const { startX, startY } = createParticle(i);
        
        return (
          <motion.div
            key={i}
            className="absolute"
            initial={{
              x: startX,
              y: startY,
              scale: 0,
              rotate: 0
            }}
            animate={{
              y: [null, startY - 250, startY - 500],
              x: [
                null, 
                startX + (Math.random() * 150 - 75),
                startX + (Math.random() * 200 - 100)
              ],
              scale: [0, 1.2, 0],
              rotate: [0, 360, 720],
              opacity: [0, 0.9, 0]
            }}
            transition={{
              duration: Math.random() * 3 + 4,
              repeat: Infinity,
              delay: Math.random() * 4,
              ease: "easeInOut"
            }}
          >
            {i % 4 === 0 && <Star className="w-4 h-4 text-pink-400 drop-shadow-lg" />}
            {i % 4 === 1 && <Heart className="w-4 h-4 text-red-400 drop-shadow-lg" />}
            {i % 4 === 2 && <Music2 className="w-4 h-4 text-purple-400 drop-shadow-lg" />}
            {i % 4 === 3 && <div className="w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full drop-shadow-lg" />}
          </motion.div>
        );
      })}
    </div>
  );
};

// Vibrant animated background with stage lighting effects
const StageBackground = () => {
  return (
    <div className="fixed inset-0 -z-10">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900" />
      
      {/* Animated spotlight effects */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            "radial-gradient(circle at 20% 20%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)",
            "radial-gradient(circle at 80% 30%, rgba(168, 85, 247, 0.4) 0%, transparent 50%)",
            "radial-gradient(circle at 60% 70%, rgba(251, 146, 60, 0.3) 0%, transparent 50%)",
            "radial-gradient(circle at 30% 80%, rgba(34, 197, 94, 0.3) 0%, transparent 50%)",
            "radial-gradient(circle at 20% 20%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)"
          ]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />
      
      {/* Stage floor reflection */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      
      {/* Disco ball effect */}
      <motion.div
        className="absolute top-20 right-20 w-32 h-32 opacity-20"
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      >
        <div className="w-full h-full bg-gradient-to-br from-yellow-300 to-pink-400 rounded-full blur-xl" />
      </motion.div>
    </div>
  );
};

// Pulsing rhythm component
const RhythmPulse = ({ children, delay = 0 }) => {
  return (
    <motion.div
      animate={{
        scale: [1, 1.02, 1],
        filter: ["brightness(1)", "brightness(1.1)", "brightness(1)"]
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        delay,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );
};

// Dynamic card with stage lighting
const StageCard = ({ children, className = "", glowColor = "pink" }) => {
  const glowColors = {
    pink: "shadow-pink-500/30",
    purple: "shadow-purple-500/30",
    orange: "shadow-orange-500/25",
    green: "shadow-green-500/25",
    blue: "shadow-blue-500/25"
  };

  const hoverShadows = {
    pink: 'rgba(236, 72, 153, 0.4)',
    purple: 'rgba(168, 85, 247, 0.4)',
    orange: 'rgba(251, 146, 60, 0.35)',
    green: 'rgba(34, 197, 94, 0.35)',
    blue: 'rgba(59, 130, 246, 0.35)'
  };

  return (
    <motion.div
      whileHover={{ 
        scale: 1.05,
        boxShadow: `0 25px 50px -12px ${hoverShadows[glowColor]}`,
        rotateY: 5
      }}
      transition={{ duration: 0.3 }}
      className={`relative bg-black/40 backdrop-blur-xl border border-white/20 rounded-3xl ${glowColors[glowColor]} ${className}`}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />
      <div className="relative">{children}</div>
    </motion.div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, role, isLoading: authLoading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, -100]);
  const y2 = useTransform(scrollY, [0, 500], [0, -200]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    const handleRedirect = async () => {
      if (isAuthenticated) {
        // Use role from AuthContext (which comes from DynamoDB user profile)
        // No need to check Cognito groups directly as new users may not have them
        console.log('HomePage redirect - User role:', role);
        
        if (role === 'Admin') {
          navigate("/admin/dashboard");
        } else if (role === 'Judge') {
          navigate("/admin/limited-dashboard");
        } else {
          // For regular users (studioOwner, teacher, dancer, parent) or users without specific roles
          navigate("/user/dashboard");
        }
      } else {
        setIsLoading(false);
      }
    };
    
    handleRedirect();
  }, [isAuthenticated, role, authLoading, navigate]);

  const features = [
    {
      title: 'AI-Powered Scoring',
      icon: Cpu,
      description: 'Revolutionary AI that understands dance technique, artistry, and performance quality',
      color: 'purple'
    },
    {
      title: 'Live Performance Streaming',
      icon: Video,
      description: 'Broadcast competitions worldwide with professional-grade streaming technology',
      color: 'pink'
    },
    {
      title: 'Real-time Judge Sync',
      icon: Zap,
      description: 'Instant score synchronization across all judge stations with zero lag',
      color: 'orange'
    },
    {
      title: 'Global Dance Network',
      icon: Globe,
      description: 'Connect with dancers, studios, and competitions from around the world',
      color: 'green'
    }
  ];

  const stats = [
    { number: '50K+', label: 'Dancers', icon: Users, color: 'pink' },
    { number: '99.9%', label: 'Uptime', icon: TrendingUp, color: 'purple' },
    { number: '150+', label: 'Countries', icon: Globe, color: 'orange' },
    { number: '24/7', label: 'Support', icon: Heart, color: 'green' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center">
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            rotate: { duration: 1, repeat: Infinity, ease: "linear" },
            scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
          }}
          className="w-16 h-16 border-4 border-pink-500/30 border-t-pink-500 rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <StageBackground />
      <DanceParticles />

      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={`fixed top-0 w-full backdrop-blur-2xl border-b z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-black/80 border-pink-500/30' 
            : 'bg-transparent border-transparent'
        }`}
      >
        <nav className="w-full px-8 lg:px-16 xl:px-20 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 10 }}
                className="relative"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center border border-pink-400/50 shadow-lg shadow-pink-500/25">
                  <img 
                    src={ddLogo}
                    alt="Dance Digital"
                    className="w-7 h-7 object-contain filter brightness-0 invert"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition-opacity" />
              </motion.div>
              <span className="text-2xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent">
                Dance Digital
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {['Features', 'Pricing', 'About', 'Contact'].map((item) => (
                <Link
                  key={item}
                  to={`/${item.toLowerCase()}`}
                  className="relative text-white/80 hover:text-white transition-colors group text-lg font-medium"
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-400 to-purple-400 group-hover:w-full transition-all duration-300" />
                </Link>
              ))}
              <Link 
                to="/auth?state=signIn" 
                className="text-white/80 hover:text-white transition-colors text-lg font-medium"
              >
                Sign in
              </Link>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/auth?state=signUp" 
                  className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-xl hover:shadow-pink-500/25 transition-all duration-300 flex items-center space-x-2 border border-pink-400/50 text-lg font-semibold"
                >
                  <span>Get Started</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-white/90 hover:text-white"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="lg:hidden mt-4 space-y-4 pb-4"
              >
                {['Features', 'Pricing', 'About', 'Contact'].map((item) => (
                  <Link
                    key={item}
                    to={`/${item.toLowerCase()}`}
                    className="block py-3 text-white/80 hover:text-white transition-colors text-lg"
                  >
                    {item}
                  </Link>
                ))}
                <Link to="/auth?state=signIn" className="block py-3 text-white/90 hover:text-white transition-colors text-lg">
                  Sign in
                </Link>
                <Link 
                  to="/auth?state=signUp" 
                  className="block w-full px-6 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl text-center border border-pink-400/50 text-lg font-semibold"
                >
                  Get Started
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </motion.header>

      {/* Hero Section - Fixed Alignment and Spacing */}
      <section className="relative pt-20 pb-16 overflow-hidden min-h-screen flex items-center z-20">
        <div className="w-full px-8 lg:px-16 xl:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[85vh]">
              {/* Left Column - FIXED ALIGNMENT */}
              <div className="flex items-center justify-start relative z-30">
                <div className="text-left w-full max-w-xl">
                  {/* Badge - Aligned to consistent left margin */}
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="inline-flex items-center px-5 py-2.5 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/40 text-base text-pink-200 mb-8 backdrop-blur-xl"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    <span className="font-semibold">AI-Powered Dance Scoring</span>
                  </motion.div>

                  {/* Headline - Better line spacing and consistent alignment */}
                  <motion.h1 
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[0.95] mb-8"
                  >
                    <RhythmPulse delay={0}>
                      <span className="block text-white drop-shadow-2xl whitespace-nowrap">
                        DANCE EVENTS
                      </span>
                    </RhythmPulse>
                    <RhythmPulse delay={0.5}>
                      <span className="block bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent drop-shadow-2xl mt-2">
                        REIMAGINED
                      </span>
                    </RhythmPulse>
                  </motion.h1>

                  {/* Body text - Proper width and alignment */}
                  <motion.p 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="text-lg lg:text-xl text-white/90 font-medium leading-relaxed max-w-lg mb-10"
                  >
                    The future of dance competitions is here. Experience revolutionary 
                    <span className="text-pink-300 font-bold"> AI scoring</span>, 
                    <span className="text-purple-300 font-bold"> live streaming</span>, and 
                    <span className="text-orange-300 font-bold"> real-time analytics </span> 
                    that brings the energy of the stage to every screen.
                  </motion.p>
                  
                  {/* Buttons - Aligned to content */}
                  <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    className="flex flex-col sm:flex-row gap-4 mb-12"
                  >
                    <motion.div 
                      whileHover={{ scale: 1.05, y: -3 }} 
                      whileTap={{ scale: 0.95 }}
                      className="group"
                    >
                      <Link 
                        to="/auth?state=signUp" 
                        className="px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 flex items-center justify-center space-x-2 text-lg font-bold border border-pink-400/50 relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                        <Rocket className="w-5 h-5" />
                        <span>Start Free Trial</span>
                      </Link>
                    </motion.div>
                    <motion.div 
                      whileHover={{ scale: 1.05, y: -3 }} 
                      whileTap={{ scale: 0.95 }}
                      className="group"
                    >
                      <Link 
                        to="/judge-station/1" 
                        className="px-8 py-4 bg-white/10 backdrop-blur-xl text-white rounded-2xl hover:bg-white/20 transition-all duration-300 flex items-center justify-center space-x-2 text-lg font-bold border border-white/30 relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <Play className="w-5 h-5" />
                        <span>Watch Demo</span>
                      </Link>
                    </motion.div>
                  </motion.div>

                  {/* Trust indicators - Properly integrated */}
                  <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                    className="border-t border-white/10 pt-8"
                  >
                    {/* Mobile: Horizontal layout with titles underneath */}
                    <div className="grid grid-cols-3 sm:grid-cols-3 gap-4 sm:gap-6">
                      <div className="flex flex-col items-center space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-xl flex items-center justify-center border border-green-400/30">
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                        <div className="text-center sm:text-left">
                          <div className="text-white font-semibold text-sm sm:text-base">Live Now</div>
                          <div className="text-white/60 text-xs sm:text-sm">Active Competitions</div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-center space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-500/20 to-pink-600/20 rounded-xl flex items-center justify-center border border-pink-400/30">
                          <Users className="w-6 h-6 text-pink-300" />
                        </div>
                        <div className="text-center sm:text-left">
                          <div className="text-white font-semibold text-sm sm:text-base">50K+</div>
                          <div className="text-white/60 text-xs sm:text-sm">Active Users</div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-center space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl flex items-center justify-center border border-purple-400/30">
                          <Shield className="w-6 h-6 text-purple-300" />
                        </div>
                        <div className="text-center sm:text-left">
                          <div className="text-white font-semibold text-sm sm:text-base">Enterprise</div>
                          <div className="text-white/60 text-xs sm:text-sm">Security</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
              
              {/* Right Column - Dashboard Showcase - Fixed positioning */}
              <motion.div 
                style={{ y: y1 }}
                className="relative w-full pt-8 z-30"
              >
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                  transition={{ duration: 1.2, delay: 0.6 }}
                  className="relative perspective-1000 w-full"
                >
                  <StageCard className="p-6 lg:p-8 transform hover:rotate-1 transition-transform duration-500 w-full" glowColor="pink">
                    <div className="space-y-6">
                      {/* Header with live indicator */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex space-x-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          </div>
                          <span className="text-base lg:text-lg font-bold text-white">Judge Station Pro</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <motion.div 
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="w-2.5 h-2.5 bg-red-500 rounded-full"
                          />
                          <span className="text-pink-300 font-bold text-sm lg:text-base">LIVE</span>
                        </div>
                      </div>

                      {/* Live Performance Video */}
                      <div className="aspect-video bg-gradient-to-br from-purple-600/40 to-pink-600/40 rounded-2xl border border-pink-400/40 flex items-center justify-center relative overflow-hidden">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="w-14 lg:w-16 h-14 lg:h-16 bg-pink-500/30 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer border border-pink-400/60"
                        >
                          <Play className="w-7 lg:w-8 h-7 lg:h-8 text-white ml-0.5" />
                        </motion.div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white font-bold text-sm lg:text-base">Contemporary Solo</div>
                              <div className="text-pink-200 text-xs lg:text-sm">Emma Johnson - Studio Elite</div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Volume2 className="w-4 h-4 text-pink-300" />
                              <div className="flex space-x-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <motion.div
                                    key={i}
                                    animate={{ height: [6, 16, 6] }}
                                    transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
                                    className="w-0.5 bg-pink-400 rounded-full"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Live Scoring Interface */}
                      <div className="grid grid-cols-3 gap-3 lg:gap-4">
                        {[
                          { category: 'Technique', score: 9.2, color: 'from-purple-500 to-purple-600' },
                          { category: 'Performance', score: 9.7, color: 'from-pink-500 to-pink-600' },
                          { category: 'Choreography', score: 9.4, color: 'from-orange-500 to-orange-600' }
                        ].map(({ category, score, color }, idx) => (
                          <motion.div 
                            key={category}
                            whileHover={{ scale: 1.05, y: -3 }}
                            animate={{ 
                              boxShadow: [
                                '0 0 15px rgba(236, 72, 153, 0.3)',
                                '0 0 25px rgba(236, 72, 153, 0.5)',
                                '0 0 15px rgba(236, 72, 153, 0.3)'
                              ]
                            }}
                            transition={{ 
                              boxShadow: { duration: 2, repeat: Infinity, delay: idx * 0.3 }
                            }}
                            className={`p-4 lg:p-5 bg-gradient-to-br ${color} rounded-xl border border-white/20 cursor-pointer relative overflow-hidden`}
                          >
                            <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition-opacity" />
                            <div className="relative">
                              <div className="text-white/80 mb-1 font-medium text-xs lg:text-sm">{category}</div>
                              <motion.div 
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5, delay: 1 + idx * 0.2 }}
                                className="text-2xl lg:text-3xl font-black text-white"
                              >
                                {score}
                              </motion.div>
                              <div className="text-white/70 text-xs">Excellent</div>
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Performance Queue */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-white font-bold text-sm lg:text-base">Up Next</div>
                          <div className="text-pink-300 text-xs lg:text-sm">3 performances</div>
                        </div>
                        {[
                          { name: 'Hip Hop Crew Battle', studio: 'Urban Dance Co.', time: '2:30' },
                          { name: 'Ballet Variations', studio: 'Classical Academy', time: '4:15' },
                          { name: 'Jazz Fusion', studio: 'Rhythm Studios', time: '3:45' }
                        ].map((performance, idx) => (
                          <motion.div 
                            key={performance.name}
                            initial={{ x: -30, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 1.5 + idx * 0.2 }}
                            whileHover={{ x: 5, backgroundColor: 'rgba(236, 72, 153, 0.1)' }}
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10 hover:border-pink-400/40 transition-all cursor-pointer"
                          >
                            <div>
                              <div className="text-white font-semibold text-xs lg:text-sm">{performance.name}</div>
                              <div className="text-white/60 text-xs">{performance.studio}</div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="text-pink-300 font-mono text-xs">{performance.time}</div>
                              <div className="w-6 h-6 bg-pink-500/20 rounded-full flex items-center justify-center">
                                <span className="text-pink-300 font-bold text-xs">#{101 + idx}</span>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </StageCard>

                  {/* Floating Action Elements - Fixed positioning to avoid header clipping */}
                  <motion.div 
                    animate={{ 
                      y: [0, -8, 0], 
                      rotate: [0, 8, 0],
                      scale: [1, 1.05, 1]
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute top-4 -right-3 w-16 lg:w-20 h-16 lg:h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl border border-pink-400/50"
                  >
                    <Trophy className="w-8 lg:w-10 h-8 lg:h-10 text-white" />
                  </motion.div>

                  <motion.div 
                    animate={{ 
                      y: [0, 12, 0], 
                      rotate: [0, -8, 0],
                      scale: [1, 1.05, 1]
                    }}
                    transition={{ duration: 5, repeat: Infinity }}
                    className="absolute -bottom-3 -left-3 w-14 lg:w-16 h-14 lg:h-16 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center shadow-xl border border-orange-400/50"
                  >
                    <Music2 className="w-7 lg:w-8 h-7 lg:h-8 text-white" />
                  </motion.div>

                  <motion.div 
                    animate={{ 
                      y: [0, -8, 0], 
                      x: [0, 3, 0],
                      scale: [1, 1.03, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute top-1/2 -right-4 w-12 lg:w-14 h-12 lg:h-14 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center shadow-xl border border-green-400/50"
                  >
                    <Camera className="w-6 lg:w-7 h-6 lg:h-7 text-white" />
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-pink-500/20">
        <div className="w-full px-8 lg:px-16 xl:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map(({ number, label, icon: Icon, color }, index) => (
                <motion.div 
                  key={label}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <RhythmPulse delay={index * 0.2}>
                    <div className={`w-16 h-16 bg-gradient-to-br ${
                      color === 'pink' ? 'from-pink-500/40 to-pink-600/40 border-pink-400/40' :
                      color === 'purple' ? 'from-purple-500/40 to-purple-600/40 border-purple-400/40' :
                      color === 'orange' ? 'from-orange-500/40 to-orange-600/40 border-orange-400/40' :
                      'from-green-500/40 to-green-600/40 border-green-400/40'
                    } rounded-2xl flex items-center justify-center mx-auto mb-4 border backdrop-blur-xl group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-8 h-8 ${
                        color === 'pink' ? 'text-pink-300' :
                        color === 'purple' ? 'text-purple-300' :
                        color === 'orange' ? 'text-orange-300' :
                        'text-green-300'
                      }`} />
                    </div>
                  </RhythmPulse>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="text-3xl lg:text-4xl font-black bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent mb-2"
                  >
                    {number}
                  </motion.div>
                  <div className="text-white/80 text-base font-medium">{label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative">
        <div className="w-full px-8 lg:px-16 xl:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.h2 
                className="text-4xl lg:text-6xl font-black mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <span className="text-white">Powered by</span>
                <span className="block bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
                  NEXT-GEN TECH
                </span>
              </motion.h2>
              <motion.p 
                className="text-xl text-white/80 font-medium max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Revolutionary technology that understands the art, passion, and precision of dance
              </motion.p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {features.map(({ title, icon: Icon, description, color }, index) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 50, rotateY: -10 }}
                  animate={{ opacity: 1, y: 0, rotateY: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ y: -5, rotateY: 3 }}
                >
                  <StageCard className="p-8 h-full group" glowColor={color}>
                    <div className="flex items-start space-x-5">
                      <RhythmPulse delay={index * 0.3}>
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br ${
                          color === 'purple' ? 'from-purple-500/40 to-purple-600/40 border-purple-400/40' :
                          color === 'pink' ? 'from-pink-500/40 to-pink-600/40 border-pink-400/40' :
                          color === 'orange' ? 'from-orange-500/40 to-orange-600/40 border-orange-400/40' :
                          'from-green-500/40 to-green-600/40 border-green-400/40'
                        } border backdrop-blur-xl group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className={`w-8 h-8 ${
                            color === 'purple' ? 'text-purple-300' :
                            color === 'pink' ? 'text-pink-300' :
                            color === 'orange' ? 'text-orange-300' :
                            'text-green-300'
                          }`} />
                        </div>
                      </RhythmPulse>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
                        <p className="text-white/70 leading-relaxed">{description}</p>
                      </div>
                    </div>
                  </StageCard>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-purple-900/60 via-pink-900/40 to-orange-900/60 relative">
        {/* Additional background effects */}
        <div className="absolute inset-0">
          <motion.div
            animate={{
              background: [
                "radial-gradient(circle at 30% 40%, rgba(236, 72, 153, 0.2) 0%, transparent 70%)",
                "radial-gradient(circle at 70% 60%, rgba(168, 85, 247, 0.3) 0%, transparent 70%)",
                "radial-gradient(circle at 30% 40%, rgba(236, 72, 153, 0.2) 0%, transparent 70%)"
              ]
            }}
            transition={{ duration: 6, repeat: Infinity }}
            className="absolute inset-0"
          />
        </div>
        
        <div className="w-full px-8 lg:px-16 xl:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 xl:gap-12 items-center overflow-visible">
              {/* Left - Enhanced Interactive Demo */}
              <div className="relative w-full flex justify-center">
                <div className="relative max-w-md lg:max-w-lg">
                  <StageCard className="p-4 lg:p-6 xl:p-8" glowColor="orange">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg lg:text-xl font-bold text-white">Live Judge Station</h3>
                        <div className="flex items-center space-x-2">
                          <motion.div 
                            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="w-3 h-3 bg-red-500 rounded-full"
                          />
                          <span className="text-red-300 font-bold text-sm">RECORDING</span>
                        </div>
                      </div>

                      {/* Enhanced Scoring Interface */}
                      <div className="grid grid-cols-3 gap-3 lg:gap-4">
                        {['Technique', 'Performance', 'Choreography'].map((category, idx) => (
                          <motion.div 
                            key={category}
                            whileHover={{ scale: 1.05, y: -3 }}
                            animate={{ 
                              boxShadow: [
                                '0 0 15px rgba(251, 146, 60, 0.3)',
                                '0 0 25px rgba(251, 146, 60, 0.6)',
                                '0 0 15px rgba(251, 146, 60, 0.3)'
                              ]
                            }}
                            transition={{ 
                              boxShadow: { duration: 2, repeat: Infinity, delay: idx * 0.4 }
                            }}
                            className="p-3 lg:p-4 bg-gradient-to-br from-orange-500/40 to-pink-500/40 rounded-xl border border-orange-400/40 cursor-pointer relative overflow-hidden"
                          >
                            <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition-opacity" />
                            <div className="relative">
                              <div className="text-orange-200/80 mb-1 lg:mb-2 font-medium text-xs lg:text-sm">{category}</div>
                              <div className="text-xl lg:text-2xl font-black text-white">{8.5 + idx * 0.3}</div>
                              <div className="text-orange-200/70 text-xs">Excellent</div>
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Enhanced Performance Video */}
                      <div className="aspect-video bg-gradient-to-br from-purple-600/40 to-pink-600/40 rounded-2xl border border-pink-400/40 flex items-center justify-center relative overflow-hidden">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="w-12 lg:w-16 h-12 lg:h-16 bg-pink-500/40 backdrop-blur-xl rounded-full flex items-center justify-center cursor-pointer border border-pink-400/60 group"
                        >
                          <Play className="w-6 lg:w-8 h-6 lg:h-8 text-white ml-0.5 group-hover:scale-110 transition-transform" />
                        </motion.div>
                        <div className="absolute bottom-3 lg:bottom-4 left-3 lg:left-4 right-3 lg:right-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white font-bold text-sm lg:text-base">Contemporary Solo</div>
                              <div className="text-pink-200 text-xs lg:text-sm">Emma Johnson - Studio Elite</div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Volume2 className="w-3 lg:w-4 h-3 lg:h-4 text-pink-300" />
                              <div className="flex space-x-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <motion.div
                                    key={i}
                                    animate={{ height: [6, 16, 6] }}
                                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.1 }}
                                    className="w-0.5 lg:w-1 bg-pink-400 rounded-full"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </StageCard>

                  {/* Floating elements */}
                  <motion.div 
                    animate={{ 
                      y: [0, -15, 0], 
                      rotate: [0, 12, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 5, repeat: Infinity }}
                    className="absolute -top-4 -right-4 w-14 lg:w-16 h-14 lg:h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl border border-pink-400/50"
                  >
                    <Trophy className="w-6 lg:w-8 h-6 lg:h-8 text-white" />
                  </motion.div>

                  <motion.div 
                    animate={{ 
                      y: [0, 15, 0], 
                      rotate: [0, -12, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 6, repeat: Infinity }}
                    className="absolute -bottom-4 -left-4 w-12 lg:w-14 h-12 lg:h-14 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center shadow-xl border border-orange-400/50"
                  >
                    <Music2 className="w-5 lg:w-6 h-5 lg:h-6 text-white" />
                  </motion.div>
                </div>
              </div>

              {/* Right - Enhanced Content */}
              <div className="space-y-6 max-w-xl">
                <motion.h2 
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-3xl lg:text-4xl xl:text-5xl font-black"
                >
                  <span className="text-white">Experience the</span>
                  <span className="block bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                    FUTURE OF JUDGING
                  </span>
                </motion.h2>
                <motion.p 
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-lg text-white/80 font-medium leading-relaxed"
                >
                  Our revolutionary judge interface brings the energy of live performance 
                  to every screen. Real-time scoring, HD video, and instant feedback 
                  create an immersive judging experience like never before.
                </motion.p>
                <div className="space-y-4">
                  {[
                    'Real-time score synchronization across all devices',
                    'Ultra-HD video playback with slow-motion review',
                    'Instant feedback submission and analytics',
                    'Mobile-responsive design for any device'
                  ].map((feature, idx) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + idx * 0.1 }}
                      className="flex items-center space-x-3 group"
                    >
                      <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <ChevronRight className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white/90 font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>
                <motion.div 
                  className="pt-4"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link 
                    to="/judge-station/1" 
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-2xl hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 space-x-2 border border-orange-400/50 text-lg font-bold"
                  >
                    <span>Try Live Demo</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-900/30 via-purple-900/40 to-orange-900/30" />
        <div className="w-full px-8 lg:px-16 xl:px-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <motion.h2 
                className="text-4xl lg:text-6xl font-black mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <span className="text-white">Ready to Transform</span>
                <span className="block bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
                  YOUR COMPETITIONS?
                </span>
              </motion.h2>
              <motion.p 
                className="text-xl text-white/80 mb-12 font-medium leading-relaxed max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Join thousands of dance professionals already using Dance Digital to create 
                unforgettable competition experiences that celebrate the art of movement.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-6 justify-center"
              >
                <motion.div 
                  whileHover={{ scale: 1.05, y: -3 }} 
                  whileTap={{ scale: 0.95 }}
                  className="group"
                >
                  <Link 
                    to="/auth?state=signUp" 
                    className="px-10 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-xl hover:shadow-pink-500/30 transition-all duration-300 flex items-center justify-center space-x-3 text-lg font-bold border border-pink-400/50 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                    <Rocket className="w-5 h-5" />
                    <span>Start Free Trial</span>
                  </Link>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, y: -3 }} 
                  whileTap={{ scale: 0.95 }}
                  className="group"
                >
                  <Link 
                    to="/contact" 
                    className="px-10 py-4 bg-white/10 backdrop-blur-xl text-white rounded-2xl hover:bg-white/20 transition-all duration-300 flex items-center justify-center space-x-3 text-lg font-bold border border-white/30 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>Contact Sales</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-pink-500/30 bg-black/90 backdrop-blur-xl relative">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-pink-900/20" />
        <div className="w-full px-8 lg:px-16 xl:px-20 py-12">
          <div className="max-w-7xl mx-auto relative">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              {[
                {
                  title: 'Platform',
                  links: ['Features', 'Pricing', 'Integrations', 'Enterprise']
                },
                {
                  title: 'Company',
                  links: ['About Us', 'Customers', 'Blog', 'Careers']
                },
                {
                  title: 'Resources',
                  links: ['Documentation', 'API', 'Community', 'Support']
                },
                {
                  title: 'Legal',
                  links: ['Privacy Policy', 'Terms of Service', 'Compliance', 'Security']
                }
              ].map((section) => (
                <div key={section.title}>
                  <h3 className="text-lg font-bold mb-4 text-white">{section.title}</h3>
                  <ul className="space-y-2">
                    {section.links.map((link) => (
                      <li key={link}>
                        <Link 
                          to={`/${link.toLowerCase().replace(' ', '-')}`} 
                          className="text-white/60 hover:text-pink-300 transition-colors hover:translate-x-1 transform duration-200 block"
                        >
                          {link}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col items-center border-t border-pink-500/30 pt-8">
              <div className="flex space-x-6 mb-6">
                {[Twitter, Linkedin, Instagram].map((Icon, index) => (
                  <motion.a 
                    key={index}
                    whileHover={{ scale: 1.2, rotate: 10, y: -3 }}
                    href="#"
                    className="w-12 h-12 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-full flex items-center justify-center text-pink-200/70 hover:text-pink-100 hover:bg-pink-500/30 transition-all duration-300 border border-pink-400/40 backdrop-blur-xl"
                  >
                    <Icon size={20} />
                  </motion.a>
                ))}
              </div>
              
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center border border-pink-400/50 shadow-lg shadow-pink-500/25">
                  <img
                    src={ddLogo}
                    alt="Dance Digital"
                    className="w-6 h-6 object-contain filter brightness-0 invert"
                  />
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent">
                  Dance Digital
                </span>
              </div>
              <p className="text-white/60 text-center">
                © 2025 Dance Digital. All rights reserved. Built with ❤️ for the dance community.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;